/* eslint-disable jsdoc/no-undefined-types */
import {
    MessageBar, MessageBarType, PrimaryButton, DefaultButton, Spinner, Text,
    TooltipHost,
    Checkbox,
    Dialog,
    DialogType,
    DialogFooter,
    SpinnerSize,
} from '@fluentui/react'
import React, {
    useState, useCallback, useEffect, useRef,
    useMemo,
} from 'react'
import store from 'stores/store'
import { setMessageBar } from 'stores/slices/common'
import CancelRequestError from 'requests/errors/cancelRequestError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import NotImplementedError from 'requests/errors/notImplementedError'
import CandidatesHandler from 'requests/handlers/candidatesHandler'
import Candidate from 'requests/objects/candidate'
import { Link } from 'react-router-dom'
import FilteredVirtualCombobox from 'components/inputs/filteredVirtualCombobox'
import styles from 'styles/components/pages/home/candidatesDuplicated.module.scss'
import EProfile from 'types/profile'
import Status from 'types/status'

const candidatesHandler = new CandidatesHandler()

const formatNbr = nbr => new Intl.NumberFormat('FR-fr').format(nbr)

/**
 * @typedef {Pick<Candidate, 'candidateId' | 'firstname' | 'lastname' | 'email' | 'duplicateExclusion'>
 *  & { isKept: boolean, replacedByCandidateId?: number }} DuplicatedCandidate
 */

/**
 * Component to display duplicated candidates
 * @param {object} props Props
 * @param {import('requests/objects/user').default} props.me Current user
 * @param {import('stores/slices/common').CommonState['messageBar']} props.messageBar Message bar state
 * @returns {JSX.Element} JSX Element
 */
export default function CandidatesDuplicated({ me, messageBar }) {
    /** @type {[DuplicatedCandidate[][], React.Dispatch<React.SetStateAction<DuplicatedCandidate[][]>>]} */
    const [duplicatedCandidates, setDuplicatedCandidates] = useState([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [page, setPage] = useState(0)
    const [status, setStatus] = useState(Status.IDLE)

    /** @type {React.MutableRefObject<import('requests/apiHandler').RequestApi<Candidate[][]>>} */
    const candidatesHandlerGetDuplicated = useRef(null)
    /** @type {React.MutableRefObject<import('requests/apiHandler').RequestApi<void>>} */
    const candidatesHandlerMergeDuplicated = useRef(null)

    const duplicatesCount = useMemo(() => duplicatedCandidates.flatMap(x => x).length, [duplicatedCandidates])

    useEffect(() => {
        (async () => {
            setStatus(Status.PENDING)

            try {
                candidatesHandlerGetDuplicated.current = candidatesHandler.getDuplicates()
                const result = await candidatesHandlerGetDuplicated.current.fetch()

                setDuplicatedCandidates(result
                    .map(group => group
                        .map(candidate => ({
                            ...candidate,
                            isKept: false,
                            replacedByCandidateId: null,
                        }))))
                setStatus(Status.RESOLVED)
            } catch (error) {
                switch (error?.constructor) {
                    case CancelRequestError:
                    case UnauthorizedError:
                    case InvalidEntityError: break
                    case NotImplementedError:
                    default:
                        setStatus(Status.REJECTED)
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                }
            }
        })()

        return () => {
            candidatesHandlerGetDuplicated.current?.cancel()
            candidatesHandlerMergeDuplicated.current?.cancel()
        }
    }, [])

    const onSubmit = useCallback(async ev => {
        ev.preventDefault()

        setStatus(Status.PENDING)
        store.dispatch(setMessageBar({ isDisplayed: false }))

        try {
            candidatesHandlerMergeDuplicated.current = candidatesHandler.mergeDuplicates(duplicatedCandidates[page])
            await candidatesHandlerMergeDuplicated.current.fetch()

            /** IDs of the candidates that were deleted in the current page */
            const candidatesIdDeleted = duplicatedCandidates[page]
                .filter(candidate => !candidate.isKept)
                .map(candidate => candidate.candidateId)

            /** IDs of the candidates that were kept in the current page */
            const candidatesIdKept = duplicatedCandidates[page]
                .filter(candidate => candidate.isKept)
                .map(candidate => candidate.candidateId)

            const newDuplicatedCandidates = duplicatedCandidates
                // Remove the current page from the list of duplicated candidates
                .filter((_, i) => i !== page)
                .map(group => group
                    // Filter out candidates that were deleted
                    .filter(candidate => !candidatesIdDeleted.includes(candidate.candidateId))
                    // Mark candidates that were kept in the current page as duplicateExclusion
                    .map(candidate => (candidatesIdKept.includes(candidate.candidateId) ? { ...candidate, duplicateExclusion: true } : candidate)))
                // Filter out empty groups and groups that only contain candidates with `duplicateExclusion` set to true
                .filter(group => group.length > 0 && group.some(candidate => !candidate.duplicateExclusion))

            setDuplicatedCandidates(newDuplicatedCandidates)
            setStatus(Status.RESOLVED)
            setPage(prevPage => Math.min(prevPage, newDuplicatedCandidates.length - 1))

            store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'Les doublons ont correctement été traités' }))
        } catch (error) {
            setStatus(Status.REJECTED)
            switch (error?.constructor) {
                case CancelRequestError:
                case UnauthorizedError:
                case NotImplementedError:
                    break
                case InvalidEntityError:
                default:
                    setStatus(Status.REJECTED)
                    // eslint-disable-next-line no-console
                    console.error(error)
                    break
            }
        }
    }, [duplicatedCandidates, page])

    return (
        <>
            {me.profileId === EProfile.Administrator && (
                <TooltipHost
                    content="Candidats en doublon à traiter"
                    hostClassName={styles['candidates-duplicated-button']}
                >
                    <PrimaryButton
                        onClick={() => {
                            setIsModalOpen(true)
                            setPage(0)
                        }}
                    >
                        {status === Status.PENDING && duplicatesCount === 0
                            ? <Spinner size={SpinnerSize.small} />
                            : formatNbr(duplicatesCount)}
                    </PrimaryButton>
                </TooltipHost>

            )}
            <Dialog
                hidden={!isModalOpen}
                onDismiss={() => {
                    setIsModalOpen(false)
                    setPage(0)
                }}
                dialogContentProps={{
                    type: DialogType.close,
                    title: 'Candidats en doublon à traiter',
                    subText: duplicatedCandidates.length > 0 ? `Page ${formatNbr(page + 1)} / ${formatNbr(duplicatedCandidates.length)}` : '',
                    styles: { subText: { marginTop: '-1rem' } },
                }}
                modalProps={{
                    isBlocking: true,
                }}
                maxWidth="95vw"
                minWidth="75vw"
            >
                {messageBar?.isDisplayed
                    && (
                        <>
                            <MessageBar
                                messageBarType={messageBar.type}
                                isMultiline={false}
                                truncated
                                onDismiss={() => store.dispatch(setMessageBar({ isDisplayed: false }))}
                            >
                                {messageBar.message}
                            </MessageBar>
                            <br />
                        </>
                    )}
                {duplicatesCount === 0 && (
                    <MessageBar
                        messageBarType={MessageBarType.info}
                        isMultiline={false}
                    >
                        Vous n'avez plus de doublons à traiter.
                    </MessageBar>
                )}
                {duplicatesCount > 0 && (
                    <form
                        onSubmit={onSubmit}
                        className={styles['candidates-duplicated']}
                    >
                        <table>
                            <colgroup>
                                <col />
                                <col />
                                <col />
                                <col />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th><Text>Nom et Prénom</Text></th>
                                    <th><Text>Email</Text></th>
                                    <th><Text>Conserver</Text></th>
                                    <th><Text>Remplacer par</Text></th>
                                </tr>
                            </thead>
                            <tbody>
                                {duplicatedCandidates[page]?.map((candidate, index) => (
                                    <tr key={candidate.candidateId}>
                                        <td>
                                            <Link to={`/candidats/${candidate.candidateId}`}>
                                                {candidate.lastname}
                                                {' '}
                                                {candidate.firstname}
                                            </Link>
                                        </td>
                                        <td>
                                            <Link to={`/candidats/${candidate.candidateId}`}>
                                                {candidate.email}
                                            </Link>
                                        </td>
                                        <td>
                                            <Checkbox
                                                disabled={status === Status.PENDING}
                                                checked={candidate.isKept}
                                                onChange={(_, isChecked) => {
                                                    const newCandidates = [...duplicatedCandidates]
                                                    newCandidates[page][index].isKept = isChecked
                                                    setDuplicatedCandidates(newCandidates)
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <FilteredVirtualCombobox
                                                required
                                                disabled={candidate.isKept || !duplicatedCandidates[page]?.some(c => c.isKept) || status === Status.PENDING}
                                                options={duplicatedCandidates[page]
                                                    .filter(c => c.isKept)
                                                    .map(c => ({
                                                        key: c.candidateId,
                                                        text: `${c.lastname} ${c.firstname} (${c.email})`,
                                                    }))}
                                                selectedKey={candidate.replacedByCandidateId}
                                                onChange={(_ev, option) => {
                                                    const newCandidates = [...duplicatedCandidates]
                                                    newCandidates[page][index].replacedByCandidateId = option.key
                                                    setDuplicatedCandidates(newCandidates)
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className={styles['candidates-duplicated-footer']}>
                            <Text
                                variant="smallPlus"
                                block
                            >
                                En cliquant sur "Enregistrer" les candidats non cochés seront définitivement supprimés.
                                <br />
                                Les candidats conservés
                                n'apparaitront plus ici, sauf si un nouveau doublon potentiel est détecté.
                            </Text>
                            <PrimaryButton
                                type="submit"
                                text="Enregistrer"
                                iconProps={{ iconName: 'Save' }}
                                disabled={status === Status.PENDING || !duplicatedCandidates[page]?.some(c => c.isKept)}
                            >
                                {status === Status.PENDING && (
                                    <Spinner size={SpinnerSize.small} />
                                )}
                            </PrimaryButton>
                            <br />
                        </div>
                    </form>
                )}
                <DialogFooter>
                    <DefaultButton
                        onClick={() => setPage(prevPage => Math.max(prevPage - 1, 0))}
                        disabled={page === 0 || status === Status.PENDING}
                        text="Précédent"
                        iconProps={{ iconName: 'ChevronLeft' }}
                    />

                    <DefaultButton
                        onClick={() => setPage(prevPage => Math.min(prevPage + 1, duplicatedCandidates.length - 1))}
                        disabled={page >= duplicatedCandidates.length - 1 || status === Status.PENDING}
                        text="Suivant"
                        iconProps={{ iconName: 'ChevronRight' }}
                    />
                </DialogFooter>
            </Dialog>
        </>
    )
}
